import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { adminListCoupons, adminCreateCoupon, adminUpdateCoupon, adminDeleteCoupon } from "../../services/api";

const label = "text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-charcoal";
const input = "mt-2 w-full border border-brand-sand bg-white px-4 py-3 text-sm outline-none focus:border-brand-orange";
const EMPTY = { code: "", type: "percent", value: 0, min_order: 0, expiry: "", enabled: true };

export default function AdminCoupons() {
  const { user, ready } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // "new" | coupon_id | null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminListCoupons().then(setCoupons).catch(() => toast.error("Could not load coupons.")).finally(() => setLoading(false));
  };
  useEffect(() => { if (ready && user?.role === "admin") load(); }, [ready, user]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (c) => { setForm({ code: c.code, type: c.type, value: c.value, min_order: c.min_order, expiry: c.expiry || "", enabled: c.enabled }); setEditing(c.coupon_id); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.code.trim()) return toast.error("Coupon code is required.");
    setSaving(true);
    const payload = { ...form, value: Number(form.value) || 0, min_order: Number(form.min_order) || 0, expiry: form.expiry || null };
    try {
      if (editing === "new") await adminCreateCoupon(payload);
      else await adminUpdateCoupon(editing, payload);
      toast.success("Coupon saved.");
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon "${c.code}"?`)) return;
    try { await adminDeleteCoupon(c.coupon_id); toast.success("Coupon deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <AdminLayout title="Coupons" subtitle={`${coupons.length} coupon${coupons.length === 1 ? "" : "s"}`}
      actions={<button onClick={openNew} data-testid="coupon-add-btn" className="inline-flex items-center gap-2 bg-brand-charcoal px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream hover:bg-brand-orange"><Plus className="h-4 w-4" /> Add</button>}>
      <div data-testid="admin-coupons-page">
        {loading ? <div className="border border-brand-sand bg-brand-surface p-10 text-center text-sm text-brand-stone" data-testid="coupons-loading">Loading…</div> : coupons.length === 0 ? (
          <div className="border border-brand-sand bg-brand-surface p-10 text-center text-sm text-brand-stone" data-testid="coupons-empty">No coupons yet. Create your first coupon to offer a discount at checkout.</div>
        ) : (
          <div className="overflow-x-auto border border-brand-sand bg-brand-surface">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-brand-sand text-[11px] uppercase tracking-[0.14em] text-brand-stone">
                  <th className="p-3">Code</th><th className="p-3">Discount</th><th className="p-3">Min order</th><th className="p-3">Expiry</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.coupon_id} className="border-b border-brand-sand/60" data-testid={`coupon-row-${c.code}`}>
                    <td className="p-3 font-mono font-semibold text-brand-charcoal">{c.code}</td>
                    <td className="p-3 text-brand-charcoal">{c.type === "flat" ? `₹${c.value}` : `${c.value}%`}</td>
                    <td className="p-3 text-brand-stone">₹{c.min_order || 0}</td>
                    <td className="p-3 text-brand-stone">{c.expiry || "—"}</td>
                    <td className="p-3"><span className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${c.enabled ? "bg-brand-orange/10 text-brand-orange" : "bg-brand-sand/50 text-brand-stone"}`}>{c.enabled ? "Active" : "Off"}</span></td>
                    <td className="p-3 text-right">
                      <button onClick={() => openEdit(c)} data-testid={`coupon-edit-${c.code}`} className="mr-3 text-brand-stone hover:text-brand-orange"><Pencil className="inline h-4 w-4" /></button>
                      <button onClick={() => remove(c)} data-testid={`coupon-delete-${c.code}`} className="text-brand-stone hover:text-red-600"><Trash2 className="inline h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-charcoal/50 p-4" data-testid="coupon-modal">
          <div className="w-full max-w-md bg-brand-cream p-6">
            <p className="font-serif text-2xl text-brand-charcoal">{editing === "new" ? "New coupon" : "Edit coupon"}</p>
            <div className="mt-5 space-y-4">
              <div><label className={label}>Code</label><input data-testid="coupon-field-code" className={input} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="FESTIVE33" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={label}>Type</label>
                  <select data-testid="coupon-field-type" className={input} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    <option value="percent">Percentage %</option>
                    <option value="flat">Flat ₹</option>
                  </select>
                </div>
                <div><label className={label}>Value</label><input type="number" data-testid="coupon-field-value" className={input} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={label}>Min order (₹)</label><input type="number" data-testid="coupon-field-min" className={input} value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} /></div>
                <div><label className={label}>Expiry</label><input type="date" data-testid="coupon-field-expiry" className={input} value={form.expiry || ""} onChange={(e) => setForm({ ...form, expiry: e.target.value })} /></div>
              </div>
              <label className="flex cursor-pointer items-center gap-3 text-sm text-brand-charcoal">
                <input type="checkbox" data-testid="coupon-field-enabled" className="h-4 w-4 accent-brand-orange" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> Enabled
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} data-testid="coupon-save" className="flex-1 bg-brand-charcoal py-3 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream hover:bg-brand-orange disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
              <button onClick={close} data-testid="coupon-cancel" className="flex-1 border border-brand-sand py-3 text-xs font-semibold uppercase tracking-[0.14em] text-brand-charcoal hover:border-brand-orange">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
